<template>
  <div>
    <h2>Drug Allocations</h2>

    <div v-if="drugsWarnings.length > 0">
      <p class="text-left alert alert-danger">FYI: Multiple allocations for the same drug and animal were found. Only one allocation for each drug will be shown.</p>
      <table class="table table-bordered">
        <thead>
          <tr>
            <th>Drug Group</th>
            <th>Drug</th>
            <th>Animal</th>
            <th>Start</th>
            <th>End</th>
            <th>Qty</th>
            <th>Left</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in drugsWarnings" :key="'warn_'+index">
            <td class="text-left">{{ row["group_name"] }}</td>
            <td class="text-left">{{ row["drug_name"] }}</td>
            <td class="text-left">{{ row["animal_name"] }}</td>
            <td class="text-left">{{ row["startdate"] }}</td>
            <td class="text-left">{{ row["enddate"] }}</td>
            <td class="text-left">{{ row["quantity"] }}</td>
            <td class="text-left">{{ row["remaining"] }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="alert alert-danger" v-if="expiredAllocations.length > 0">
      <strong> Please review the allocations, some of them expired. </strong>
      <p v-for="product in expiredAllocations" :key="'expired_allocation_'+product.id">
        {{ product.name }}
      </p>
    </div>

    <div class="clearfix" :style="{ width: widthForRow(FARMS) + 'px' }">
      <div v-for="farm in FARMS" class="farm-fields" :key="farm.id">
        <h3> {{ farm.farm_label }} </h3>
        <fieldset>
          <legend> Drugs on Farm </legend>
          <radio-input label="16 Hourly milkings"
                       :field-name="`farm_${farm.id}-is_sixteen_hourly_milking`"
                       :choices="[
                                 {value: 'Yes', display: 'Yes'},
                                 {value: 'No', display: 'No'},
                                 ]"
                       :disabled="isReadOnly">
          </radio-input>

          <text-area label="What drugs are still at shed?"
                     :field-name="`farm_${farm.id}-drugs_on_farm`"
                     :disabled="isReadOnly">
          </text-area>

        </fieldset>
      </div>
    </div>

    <br>

    <input type="text" v-model="filter" class="form-control" placeholder="Search...">

    <div v-for="(drug_group, groupIndex) in getFieldValue('drug_groups')" :key="drug_group.name">
      <template v-if="![11, 12, 13].includes(drug_group.pk)">
        <h3>
          <span class="form-group">
            <span @click="toggleGroup(groupIndex)">{{ drug_group.name }}</span>
            <comments :field-name="`heading_${slugify(drug_group.name)}`" :isReadOnly="isReadOnly"></comments>
          </span>
        </h3>


        <div class="drug_group clearfix" :class="{ show: shouldShowGroup(groupIndex),  hide: !shouldShowGroup(groupIndex)  }" :style="{ width: 400 + widthForRow(FARMS) + 'px' }">

              <div class="farm-fields">
                <h4>
                  <br>
                  Quantity Purchased Last Season
                </h4>
              </div>

              <div v-for="farm in FARMS" class="farm-fields" :key="`farmtitle_${farm.id}`">
                <h4>
                  {{ farm.farm_label }}
                  <br>
                  New Season Allocations
                </h4>
                <br />
                <div class="alert alert-warning" v-if="drug_group.pk == 16 && getFieldValue(prefixedField('wants_scour_vaccine', farm.id), 'PreventiveHealthAndVacination') != 'Yes'">
                  <radio-input
                    label="Scour vaccine?"
                    :field-name="`farm_${farm.id}-wants_scour_vaccine`"
                    model-name="PreventiveHealthAndVacination"
                    subtext="Please select 'Yes' if you want to add or adjust scour vaccine allocations. An order email will be sent to retail if 'Yes' is selected. select Scour vaccine from Drug Allocations below"
                    :commentFieldName="`farm_${farm.id}-wants_scour_vaccine_comments`"
                    :addComment="true"
                    :addEmailNotification="true"
                    :choices="[
                      { value: 'Yes', display: 'Yes' },
                      { value: 'No', display: 'No' },
                    ]"
                    :disabled="isReadOnly"
                  >
                  </radio-input>
                </div>
              </div>
        </div>
        <div class="alert alert-warning" v-if="filteredDrugs(drug_group.drugs).length <= 0">
            No Results Found
        </div>
        <div v-for="drug in filteredDrugs(drug_group.drugs)" :key="drug.pk">
          <div class="clearfix drug_group"
            :class="{ show: shouldShowGroup(groupIndex),  hide: !shouldShowGroup(groupIndex)  }"
            :style="{ width: 400 + widthForRow(FARMS) + 'px' }">

            <!-- show disabled products only if they got last year data. -->
            <div v-if="drug.inactive == false || getFieldValue('last_year_usage')[drug.pk] > 0">
              <div class="farm-fields">
                <div class="form-group">
                  <label> {{ drug.name }} </label>
                  <input type="number" v-model="getFieldValue('last_year_usage')[drug.pk]" class="form-control" disabled>
                </div>
              </div>

              <div v-for="farm in FARMS" class="farm-fields" :key="`drug-${drug.pk}-${farm.id}`">

                <template v-if="drug_group.pk == 16 && getFieldValue(prefixedField('wants_scour_vaccine', farm.id), 'PreventiveHealthAndVacination') != 'Yes'">
                </template>
                <template v-else>
                  <div v-if="drug_group.pk == '15' && newAllocations[farm.id][drug.pk].is_pre_allocation" class="form-group" >
                    <label> {{ drug.name }} </label>
                    <div class="row">
                      <div class="col-md-6">
                        <input type="number"
                          v-model="newAllocations[farm.id][drug.pk].par_qty"
                          class="form-control"
                          :disabled="drug.inactive || !canSyncAllocations || isReadOnly || drug.disallow_allocations"
                          :placeholder="[ drug.inactive ? 'Disabled' : '' ]"/>
                        <span class="text-danger"> Expires {{ new Date(newAllocations[farm.id][drug.pk].par_end_date) | moment("DD-MM-YYYY") }} </span>
                      </div>

                      <div class="col-md-6">
                        <input type="number"
                          v-model="newFutureAllocations[farm.id][drug.pk].par_qty"
                          class="form-control"
                          :disabled="drug.inactive || !canSyncAllocations || isReadOnly || drug.disallow_allocations"
                          :placeholder="[ drug.inactive ? 'Disabled' : '' ]"/>

                        <span class="text-danger"> {{ futureAllocationDates(new Date(newAllocations[farm.id][drug.pk].par_end_date)) }}</span>
                      </div>
                    </div>
                  </div>
                  <div v-else class="form-group">
                    <label> {{ drug.name }} </label>
                    <input type="number"
                      v-model="newAllocations[farm.id][drug.pk].par_qty" 
                      class="form-control"
                      :disabled="drug.inactive || !canSyncAllocations || isReadOnly || drug.disallow_allocations"
                      :placeholder="[ drug.inactive ? 'Disabled' : '' ]"/>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
    <hr/>
    <text-area 
      label="Other Products Order to Retail"
      subtext="Other notes and product orders for Retail, e.g. drench, trace elements. NOT TO BE USED FOR PRESCRIBING RVMs. This order will be sent to retail by email when the consult is submitted."
      :add-email-notification="true"
      field-name="other_products_order"
      :disabled="isReadOnly">
    </text-area>

  </div>
</template>

<script>
import baseComponent from '@/mixins/base_consult_component_v2'
import { slugify } from '../../_helpers'
import { mapMutations } from 'vuex'

export default {
  name: 'DrugAllocations',
  mixins: [ baseComponent ],
  data() {
    return {
      filter: '',
      groupVisibility: {},
      newAllocations: {},
      newFutureAllocations: {},
      expiredAllocations: [],
    }
  },
  created: function() {

    for (const farm of this.FARMS) {
      this.newFutureAllocations[farm.id] = {}
      this.newAllocations[farm.id] = {}

      for (const drugGroup of this.getFieldValue('drug_groups')) {
        for (const drug of drugGroup.drugs) {
          // Initialize newAllocations model
          this.newAllocations[farm.id][drug.pk] = {
            par_qty: 0,
            drug_allocation_product: drug.pk,
            is_pre_allocation: null,
            is_future_allocation: false,
            par_end_date: null,
            drug_allocation_product_group: drugGroup.pk,
          }

          // Initialize newFutureAllocations for Critically IA GROUP
          if (drugGroup.pk == '15') {
            this.newFutureAllocations[farm.id][drug.pk] = {
              par_qty: 0,
              drug_allocation_product: drug.pk,
              is_pre_allocation: null,
              is_future_allocation: true,
              par_end_date: null,
            }
          }
        }
      }

      for (const allocation of this.getFieldValue('drugs_allocated')[farm.id]) {
        let data = {
          par_qty: allocation.par_qty,
          drug_allocation_product: allocation.drug_allocation_product,
          is_pre_allocation: allocation.is_pre_allocation,
          is_future_allocation: allocation.is_future_allocation,
          par_end_date: allocation.par_end_date,
          drug_allocation_product_group: allocation.drug_allocation_product_group,
        }
        if (allocation.is_future_allocation) {
          this.newFutureAllocations[farm.id][allocation.drug_allocation_product] = data
        } else {
          this.newAllocations[farm.id][allocation.drug_allocation_product] = data
        }
      }
    }
  },
  computed: {
    canSyncAllocations: function () {
      return !this.getFieldValue('has_synced_allocations')
    },
    searching: function () {
      return this.filter
    },
    drugsWarnings: function() {
      return Object.values(this.getChartData('all_animal_pre_allocations_ev_warn')).flat()
    }
  },
  methods: {
    ...mapMutations('currentConsult', ['updateOrCreateAllocation']),
    toggleGroup: function (groupIndex)
    {
      this.$set(this.groupVisibility, groupIndex, !this.groupVisibility[groupIndex])
    },
    shouldShowGroup: function (groupIndex)
    {
      return this.searching || Boolean(this.groupVisibility[groupIndex]);
    },
    filteredDrugs: function(drugs) 
    {
      if (this.filter) {
        let filteredDrugs = [];
        for (const drug of drugs)
        {
          if (drug.name.toLowerCase().includes(this.filter.toLowerCase()))
          {
            filteredDrugs.push(drug)
          }
        }
        return filteredDrugs;
      } else {
        return drugs
      }
    },
    slugify: function(text) 
    {
      return slugify(text)
    },
    onSubmit: function() 
    {
      for (const farm of this.getFieldValue('FARMS', 'DrugAllocations')) {
          Object.entries(this.newAllocations[farm.id] || {}).forEach(([key, data]) => {
          let farmAllocations = this.getFieldValue('drugs_allocated', 'DrugAllocations')[farm.id]

          let alloc = farmAllocations.find(alloc => (alloc.drug_allocation_product == key && alloc.is_future_allocation == false))

          if (!data.par_qty) {
            data.par_qty = 0
          }

          if (alloc) {
            alloc.par_qty = data.par_qty
          } else if (data.par_qty > 0) {
            this.updateOrCreateAllocation([farm.id, data, false])
          }
        })

        Object.entries(this.newFutureAllocations[farm.id] || {}).forEach(([key, data]) => {
          let farmAllocations = this.getFieldValue('drugs_allocated', 'DrugAllocations')[farm.id]

          let alloc = farmAllocations.find(alloc => (alloc.drug_allocation_product == key && alloc.is_future_allocation == true))

          if (!data.par_qty) {
            data.par_qty = 0
          }

          if (alloc) {
            alloc.par_qty = data.par_qty
          } else if (data.par_qty > 0) {
            this.updateOrCreateAllocation([farm.id, data, true])
          }
        })
      }
      return baseComponent.methods.onSubmit.call(this)
    },
    toggleNotifications: function() {
      this.setEmailNotification({
          slug: 'wc-other-products-order',
          value: Boolean(this.getFieldValue('other_products_order', this.$options.name)),
      })
      for (let farm of this.getFieldValue('FARMS', 'DrugAllocations')) {
        let allocations = this.newAllocations[farm.id]

        let rumenox = Object.values(allocations).filter((alloc) => alloc.par_qty > 0 && alloc.drug_allocation_product_group == 13)

        if (rumenox.length > 0) {
          this.setEmailNotification({slug: 'rumenox-non-rvm-product-order', value: true})
          break;
        } else {
          this.setEmailNotification({slug: 'rumenox-non-rvm-product-order', value: false})
        }
      }
    },
    futureAllocationDates: function(parEndDate) {
      let futureAllocationStartDate

      let today = new Date()

      if (parEndDate > today) {
        futureAllocationStartDate = this.$moment(parEndDate).add('1', 'days')
      } else {
        futureAllocationStartDate = this.$moment(today)
      }
      return futureAllocationStartDate.format("DD-MM-YYYY") + '  -  ' + futureAllocationStartDate.add('4', 'months').format("DD-MM-YYYY")
    },
  },
};
</script>

<style>
.drug_group.show { display: block;}
.drug_group.hide { display: none;}
</style>
